import { Obstacle } from "../Obstacle";
import { eObstacleType } from "../ObstacleTypes";
import { HammerObsMapData } from "./HammerObsMapData";
import { ViewCellMapRow } from "../../View/ViewCellMapRow";
import { Player } from "../../Player/Player";
import { HammerObsVer } from "./HammerObsVer";
import { Game, eModelAsset, eAudioAsset, eAtlas_0, eSfxSprite, SfxSprites } from "../../../../Game";
import { Gameplay, eBatchGroup } from "../../../Gameplay";
import { TextureAtlasUtils } from "../../../../Utils/TextuAtlasUtils";
import { GameWorld } from "../../GameWorld";
import { SoundManager } from "../../../../Utils/SoundManager";



export class HammerObs extends Obstacle {
    /**
     * Pozice kladiva (-1 = vlevo, 1 = vpravo) pri dosazeni pozice hrace na ose Z
     */
    private _targetPos: number;

    /**
     * Entita pro pohybujici se cast kladiva
     */
    private _entity2: pc.Entity;

    /**
     * Offset stinu pohybujici se casti kladiva od pozice prekazky
     */
    private static readonly _SHADOW_OFFSET = 1;

    /**
     * Entita se stinem pohybujici se casti kladiva
     */
    private _shadow2: pc.Entity;

    /**
     * Strana, na ktere se stin aktualni zobrazuje (-1 = vlevo, 1 = vpravo)
     */
    private _shadow2Side: number;

    /**
     * Lokalni pozice rotujici casti kladiva na Y
     */
    private static readonly _HAMMER_Y = 0.5;

    /**
     * Polomer hlavy kladiva
     */
    private static readonly _HEAD_RADIUS = 0.35;

    /**
     * Offset hlavy kladiva od pozice prekazky na ose X
     */
    private static readonly _HEAD_OFFSET = 1;

    /**
     * Aktualni uhel hlavy kladiva (0 = uplne vpravo, 180 = uplne vlevo)
     */
    private _headAngle: number;

    /**
     * Cyklus rotace kladiva, pri kterem byl naposled prehran zvuk uderu kladiva
     */
    private _sfxCycleId: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        super(eObstacleType.hammer);

        this._entity2 = new pc.Entity();
        this._entity2.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.hammer, pc.eAssetType.model),
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            lightmapped: false,
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.solid_0],
        });

        SoundManager.createSoundComponent(this._entity2, {
            positional: true,
            distanceModel: pc.DISTANCE_LINEAR,
            maxDistance: 10
        });

        SoundManager.addSoundSlot(this._entity2, eSfxSprite.hammerHit, SfxSprites[eSfxSprite.hammerHit], Game.sfxSpriteAssetId);

        this._entity2.setLocalPosition(0, HammerObs._HAMMER_Y, 0);
        this._entity.addChild(this._entity2);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(mapData: HammerObsMapData, row: ViewCellMapRow): void {
        super.init(mapData, row);

        // Cilova pozice
        this._targetPos = mapData.targetPos;

        let atlas = Game.getTextureAtlas(0);

        this._shadow2 = Obstacle._SHADOW_POOL.getItem();
        this._shadow2.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames[eAtlas_0.shadowSquare]).rect);
        this._shadow2.element.width = 1;
        this._shadow2.element.height = 1;

        if (this._shadow2.parent != this._entity)
            this._shadow2.reparent(this._entity);

        this._shadow2Side = 0;

        // Nastavit rotaci kladiva        
        this._sfxCycleId = -2;
        this.updateRotation();
        this._sfxCycleId = -1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    release(): void {
        super.release();

        this._shadow2.enabled = false;
        Obstacle._SHADOW_POOL.returnItem(this._shadow2);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (!super.update())
            return false;

        this.updateRotation();

        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * Nastavit rotaci kladiva
     */
    //-------------------------------------------------------------------------
    private updateRotation(): void {
        // Vypocitat za kolik vterin hrac dosahne pozice stredu prekazky na ose Z
        let pl = Player.instance;
        let pathTime = (pl.position.z - this._row.z) / pl.rollingSpeed;
        let behindPl = false;

        if (pathTime < 0) {
            pathTime *= -1;
            behindPl = true;
        }

        // Parametry rotace kladiva
        let ver = this._version as HammerObsVer;

        let flipDelay = ver.flipDelay;
        let flipDelayHalf = flipDelay / 2;
        let flipTime = ver.flipTime;
        let flipCycleLen = flipDelay + flipTime;
        let flipCyclePos = pathTime % flipCycleLen;

        // Vypocitat pozici v rotaci (0 az 1)
        let flipProgress = 0;

        if (flipCyclePos > flipDelayHalf) {
            if ((flipProgress = (flipCyclePos - flipDelayHalf) / flipTime) > 1)
                flipProgress = 1;
        }

        // Vypocitat uhel rotace
        let flipCycleId = Math.floor(pathTime / flipCycleLen);

        if (this._targetPos < 0)
            flipCycleId++;

        if (behindPl) {
            flipCycleId++;
            flipProgress = 1 - flipProgress;
        }

        flipProgress = ver.flipEase(flipProgress);

        if ((flipCycleId & 1) != 0)
            flipProgress = 1 - flipProgress;

        let headAngle = flipProgress * 180;

        this._entity2.setLocalEulerAngles(0, 0, headAngle - 90);

        // Prehrat zvukovy efekt
        let playSfx = false;

        if (this._sfxCycleId >= -1 && this._sfxCycleId != flipCycleId) {
            if (headAngle > this._headAngle) {
                if (this._headAngle < 90 && headAngle > 90)
                    playSfx = true;
            }
            else if (this._headAngle > 90 && headAngle < 90) {
                playSfx = true;
            }
        }

        if (playSfx) {
            SoundManager.playSound(this._entity2, eSfxSprite.hammerHit);
            this._sfxCycleId = flipCycleId;
        }

        this._headAngle = headAngle;

        // Stin
        if (flipProgress < 0.25 || flipProgress > 0.75) {
            // Nastavit stranu pro zobrazeni stinu
            let side = flipProgress > 0.5 ? -1 : 1;

            if (this._shadow2Side != side) {
                this._shadow2Side = side;
                this._shadow2.setLocalPosition(HammerObs._SHADOW_OFFSET * side, GameWorld.SHADOW_Y, 0);
            }

            // Vypocitat prubeh v zobrazeni stinu
            let i = (flipProgress < 0.25 ? 1 - flipProgress / 0.25 : (flipProgress - 0.75) / 0.25);
            let scale = 0.2 + 0.8 * i;

            this._shadow2.enabled = true;
            this._shadow2.setLocalScale(scale, scale, scale);
        }
        else {
            this._shadow2.enabled = false;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci 3D colizni oblast prekazky (bud pc.BoundingBox nebo pc.BoundingSphere)
     * @param pos Aktualni pozice prekazky v hernim svete
     * @param id Index kolizni oblasti
     */
    //-------------------------------------------------------------------------
    protected getColArea(pos: pc.Vec3, id: number): pc.BoundingBox | pc.BoundingSphere {
        if (id == 0)
            // Sloupek
            return super.getColArea(pos, id);

        // Hlava kladiva
        let headAngle = this._headAngle * Math.PI / 180;
        let colArea = Obstacle._TMP_BSPHERE;

        colArea.radius = HammerObs._HEAD_RADIUS;
        colArea.center.set(
            pos.x + Math.cos(headAngle) * HammerObs._HEAD_OFFSET,
            pos.y + Math.sin(headAngle) * HammerObs._HEAD_OFFSET,
            pos.z
        );

        return colArea;
    }
}